<template>
    <div class="container bg-white border rounded-3 p-4">
        <div class="form-section border rounded-3 p-3 mb-3 bg-white">
            <div class="d-flex justify-content-between align-items-center mb-2">
                <strong class="text-danger">Last Modified: {{ $formatDateTime(item.updated_at) }}</strong>
            </div>

            <div>
                <div class="row">
                    <div><strong>Patient Name:</strong> {{ item.student?.user?.name || '-' }}</div>
                    <div><strong>Admit Date:</strong> {{ $formatDate(item.admit_date) }}</div>
                </div>

                <div class="row">
                    <div><strong>Current Dose:</strong> {{ item.dose }}</div>
                    <div><strong>Time in Treatment:</strong> {{ item.time_in_treatment_days }} days</div>
                </div>

                <!-- <div class="row">
                    <div><strong>Two non-reactive drug screens in past 60 days:</strong>
                        {{ item.has_two_nonreactive_screens ? 'Yes' : 'No' }}
                    </div>
                </div> -->

                <div class="row">
                    <div><strong>Current Phase:</strong> {{ item.current_phase }}</div>
                    <div><strong>Current Schedule:</strong>
                        <span v-for="day in ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']"
                            :key="day" style="margin-right: 10px;">
                            <span
                                v-if="Array.isArray(item.current_schedule) && item.current_schedule.includes(day)">✔️</span>
                            <span v-else>⭕</span> {{ day }}
                        </span>
                    </div>
                </div>

                <div class="row">
                    <div><strong>Requested Phase:</strong> {{ item.requested_phase }}</div>
                    <div><strong>Requested Schedule:</strong>
                        <span v-for="day in ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']"
                            :key="day" style="margin-right: 10px;">
                            <span
                                v-if="Array.isArray(item.requested_schedule) && item.requested_schedule.includes(day)">✔️</span>
                            <span v-else>⭕</span> {{ day }}
                        </span>
                    </div>
                </div>

                <div v-if="editing">
                    <h4 class="mt-4">CFR 42 8.12 criteria for take-home (unsupervised) doses:</h4>

                    <div class="form-check mb-4">
                        <label class="form-label">
                            1) Absence of active substance use disorders, other physical or behavioral health conditions
                            that increase the risk of patient harm as it relates to the potential for overdose, or the
                            ability to function safely.
                        </label>
                        <div>
                            <input type="radio" v-model="takeHomeForm.no_active_substance_disorder" :value="true"
                                class="form-check-input me-1" id="no_sub_yes" />
                            <label for="no_sub_yes" class="me-3">Yes</label>

                            <input type="radio" v-model="takeHomeForm.no_active_substance_disorder" :value="false"
                                class="form-check-input me-1" id="no_sub_no" />
                            <label for="no_sub_no">No</label>
                        </div>
                    </div>

                    <div class="form-check mb-4">
                        <label class="form-label">
                            2) Regularity of attendance for supervised medication administration.
                        </label>
                        <div>
                            <input type="radio" v-model="takeHomeForm.regular_attendance" :value="true"
                                class="form-check-input me-1" id="attend_yes" />
                            <label for="attend_yes" class="me-3">Yes</label>

                            <input type="radio" v-model="takeHomeForm.regular_attendance" :value="false"
                                class="form-check-input me-1" id="attend_no" />
                            <label for="attend_no">No</label>
                        </div>
                    </div>

                    <div class="form-check mb-4">
                        <label class="form-label">
                            3) Absence of serious behavioral problems that endanger the patient, the public or others.
                        </label>
                        <div>
                            <input type="radio" v-model="takeHomeForm.no_behavioral_issues" :value="true"
                                class="form-check-input me-1" id="behav_yes" />
                            <label for="behav_yes" class="me-3">Yes</label>

                            <input type="radio" v-model="takeHomeForm.no_behavioral_issues" :value="false"
                                class="form-check-input me-1" id="behav_no" />
                            <label for="behav_no">No</label>
                        </div>
                    </div>

                    <div class="form-check mb-4">
                        <label class="form-label">
                            4) Absence of known recent diversion activity.
                        </label>
                        <div>
                            <input type="radio" v-model="takeHomeForm.no_diversion_activity" :value="true"
                                class="form-check-input me-1" id="divert_yes" />
                            <label for="divert_yes" class="me-3">Yes</label>

                            <input type="radio" v-model="takeHomeForm.no_diversion_activity" :value="false"
                                class="form-check-input me-1" id="divert_no" />
                            <label for="divert_no">No</label>
                        </div>
                    </div>

                    <div class="form-check mb-4">
                        <label class="form-label">
                            5) Whether take-home medication can be safely transported and stored.
                        </label>
                        <div>
                            <input type="radio" v-model="takeHomeForm.can_safely_store_medication" :value="true"
                                class="form-check-input me-1" id="store_yes" />
                            <label for="store_yes" class="me-3">Yes</label>

                            <input type="radio" v-model="takeHomeForm.can_safely_store_medication" :value="false"
                                class="form-check-input me-1" id="store_no" />
                            <label for="store_no">No</label>
                        </div>
                    </div>

                    <div class="form-group mt-3">
                        <label>6) Any other criteria that the medical director or medical practitioner considers
                            relevant to the patient’s safety and the public’s health.</label>
                        <textarea class="form-control" v-model="takeHomeForm.other_considerations" rows="3"
                            placeholder="(See Physician Review)"></textarea>
                    </div>
                </div>
                <div v-else>
                    <h4 class="mt-4">CFR 42 8.12 criteria for take-home (unsupervised) doses:</h4>

                    <div class="mb-3">
                        <strong>1)</strong> Absence of active substance use disorders...:
                        <span class="ms-2">{{ item.take_home_criteria?.no_active_substance_disorder === 1 ? 'Yes' : 'No'
                        }}</span>
                    </div>

                    <div class="mb-3">
                        <strong>2)</strong> Regularity of attendance for supervised medication administration:
                        <span class="ms-2">{{ item.take_home_criteria?.regular_attendance === 1 ? 'Yes' : 'No' }}</span>
                    </div>

                    <div class="mb-3">
                        <strong>3)</strong> Absence of serious behavioral problems that endanger the patient, public or
                        others:
                        <span class="ms-2">{{ item.take_home_criteria?.no_behavioral_issues === 1 ? 'Yes' : 'No'
                        }}</span>
                    </div>

                    <div class="mb-3">
                        <strong>4)</strong> Absence of known recent diversion activity:
                        <span class="ms-2">{{ item.take_home_criteria?.no_diversion_activity === 1 ? 'Yes' : 'No'
                        }}</span>
                    </div>

                    <div class="mb-3">
                        <strong>5)</strong> Whether take-home medication can be safely transported and stored:
                        <span class="ms-2">{{ item.take_home_criteria?.can_safely_store_medication === 1 ? 'Yes' : 'No'
                        }}</span>
                    </div>

                    <div class="mb-3">
                        <strong>6)</strong> Any other criteria:
                        <div class="note-box">{{ item.take_home_criteria?.other_considerations || '-' }}</div>
                    </div>
                </div>


                <h4 class="mt-3">Counselor Impressions:</h4>
                <div v-if="editImpressionId === item.id">
                    <textarea v-model="editImpression" rows="4" class="form-control mb-2"></textarea>
                    <button class="btn btn-sm btn-success me-2" @click="saveImpression(item.id)">💾 Save</button>
                    <button class="btn btn-sm btn-secondary" @click="cancelEdit">✖️ Cancel</button>
                </div>
                <div v-else class="note-box">{{ item.counselor_impression || '' }}</div>
                <button v-if="isCounselor && editImpressionId !== item.id" class="btn btn-primary mb-3"
                    id="counselor_impression_update" @click="startEditImpression(item)">
                    Update
                </button>




                <h4 class="mt-3">Practitioner Review:</h4>
                <div v-if="editing">
                    <textarea v-model="item.physician_review.review_note" rows="3" class="form-control mb-2"
                        placeholder="Enter practitioner review"></textarea>
                </div>
                <div v-else class="note-box">{{ item.physician_review?.review_note || '' }}</div>

                <h4 class="mt-3">Practitioner Determination:</h4>
                <div v-if="editing">
                    <textarea v-model="item.physician_determination.determination_note" rows="3"
                        class="form-control mb-2" placeholder="Enter determination note"></textarea>
                </div>
                <div v-else class="note-box">{{ item.physician_determination?.determination_note || '' }}</div>

                <div class="mt-2" v-if="editing">
                    <label><strong>PMP Report Reviewed:</strong></label><br>
                    <div class="form-check form-check-inline">
                        <input class="form-check-input" type="radio" :value="true"
                            v-model="item.physician_determination.pmp_report_reviewed" />
                        <label class="form-check-label"> Yes</label>
                    </div>
                    <div class="form-check form-check-inline">
                        <input class="form-check-input" type="radio" :value="false"
                            v-model="item.physician_determination.pmp_report_reviewed" />
                        <label class="form-check-label"> No</label>
                    </div>
                </div>
                <div v-else>
                    <strong>PMP Report Reviewed:</strong>
                    {{ item.physician_determination?.pmp_report_reviewed === true ? ' Yes' :
                        item.physician_determination?.pmp_report_reviewed === false ? ' No' : '' }}
                </div>

                <div class="mt-2" v-if="editing">
                    <label><strong>Result Notes:</strong></label>
                    <textarea v-model="item.physician_determination.result_notes" class="form-control"></textarea>
                </div>
                <div v-else>
                    <strong>Result Notes:</strong> {{ item.physician_determination?.result_notes || '' }}
                </div>

                <h4 class="mt-3">Approved Phase & Schedule:</h4>
                <div v-if="editing">
                    <div class="mb-2">
                        <label><strong>Approved Phase:</strong></label>
                        <select class="form-select" v-model.number="item.physician_determination.approved_phase">
                            <option disabled value="">Select Phase</option>
                            <option :value="0">Phase 0</option>
                            <option :value="1">Phase 1</option>
                            <option :value="2">Phase 2</option>
                            <option :value="3">Phase 3</option>
                            <option :value="4">Phase 4</option>
                        </select>
                    </div>

                    <div class="mb-2">
                        <label><strong>Approved Schedule Days:</strong></label><br />
                        <div class="form-check form-check-inline"
                            v-for="day in ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']"
                            :key="day">
                            <input class="form-check-input" type="checkbox" :value="day"
                                v-model="item.physician_determination.approved_schedule" />
                            <label class="form-check-label">{{ day }}</label>
                        </div>
                    </div>
                </div>
                <div v-else class="note-box">
                    {{ phaseLabel(item.physician_determination?.approved_phase) }} —
                    {{
                        Array.isArray(item.physician_determination?.approved_schedule)
                            ? item.physician_determination.approved_schedule.join(', ')
                            : '-'
                    }}
                </div>

                <div class="sign-section mt-4">
                    <div><strong>Counselor Signature:</strong> <span
                            :class="{ 'signature-font': item.counselor_id !== 0 && item.counselor_id !== null }"><u>
                                {{ item.counselor?.user?.name || '________________' }}</u>
                        </span>
                        &nbsp;&nbsp;Date: {{ item.impression_date ? $formatDate(item.impression_date) : '' }}
                    </div>
                    <div><strong>Practitioner Signature:</strong> <span
                            :class="{ 'signature-font': item.practitioner_id !== 0 && item.practitioner_id !== null }"><u>
                                {{ item.practitioner?.user?.name || '________________' }}</u>
                        </span> Date:
                        {{ $formatDate(item.practitioner_date) || '-' }}
                    </div>
                </div>
            </div>
        </div>

        <div v-if="editing">
            <div v-if="signatureData">
                <label class="form-label">Signature:</label>
                <h1 class="signature-font-lg">{{ signatureData }}</h1>
            </div>

            <div class="mb-3">
                <label class="form-label">Signed Date:</label>
                <input type="date" v-model="signedAt" class="form-control" />
            </div>

            <button class="btn btn-sm btn-success" @click="saveAllDeterminationData">
                💾 Save
            </button>
        </div>

        <button class="btn btn-sm btn-success" v-if="!editing" @click="startEditing">
            Update
        </button>
    </div>
</template>

<script>
export default {
    props: ['id'],
    data() {
        return {
            signatureData: {},
            signedAt: '',
            loading: true,
            errorMessage: '',
            item: {
                physician_determination: {},
                physician_review: {},
            },
            editing: false,
            takeHomeForm: {
                no_active_substance_disorder: false,
                regular_attendance: false,
                no_behavioral_issues: false,
                no_diversion_activity: false,
                can_safely_store_medication: false,
                other_considerations: '(See Physician Review)',
            }
        };
    },
    async mounted() {
        this.fetchSignature();
        await this.load();
        if (this.$route.query.action == 'update') {
            this.startEditing();
        }
    },
    methods: {
        phaseLabel(phase) {
            const map = {
                0: 'Phase 0',
                1: 'Phase 1',
                2: 'Phase 2',
                3: 'Phase 3',
                4: 'Phase 4',
            };
            return map[phase] || '-';
        },
        async load() {
            this.loading = true;
            try {
                const res = await this.$api.get(`/phase-determinations/${this.id}`);
                this.item = res.data || {};

                this.signedAt = this.item.practitioner_date;
                if (!this.item.physician_review) this.item.physician_review = { review_note: '' };

                this.item.physician_determination = {
                    approved_phase: this.item.physician_determination?.approved_phase ?? '',
                    approved_schedule: Array.isArray(this.item.physician_determination?.approved_schedule)
                        ? this.item.physician_determination.approved_schedule
                        : (this.item.physician_determination?.approved_schedule
                            ? JSON.parse(this.item.physician_determination.approved_schedule)
                            : []),
                    determination_note: this.item.physician_determination?.determination_note ?? '',
                    pmp_report_reviewed: this.item.physician_determination?.pmp_report_reviewed ?? null,
                    result_notes: this.item.physician_determination?.result_notes ?? '',
                };

                this.takeHomeForm = {
                    no_active_substance_disorder: Boolean(this.item.take_home_criteria?.no_active_substance_disorder ?? null),
                    regular_attendance: Boolean(this.item.take_home_criteria?.regular_attendance ?? null),
                    no_behavioral_issues: Boolean(this.item.take_home_criteria?.no_behavioral_issues ?? null),
                    no_diversion_activity: Boolean(this.item.take_home_criteria?.no_diversion_activity ?? null),
                    can_safely_store_medication: Boolean(this.item.take_home_criteria?.can_safely_store_medication ?? null),
                    other_considerations: this.item.take_home_criteria?.other_considerations ?? '',
                };
            } catch (err) {
                this.errorMessage = 'Unable to load data';
            } finally {
                this.loading = false;
            }
        },
        async fetchSignature() {
            try {
                const res = await this.$api.get('/practitioners/signature');
                this.signatureData = res.data;
                this.loading = false;
            } catch (err) {
                this.errorMessage = err.response?.data?.message || 'Failed to load signature.';
                this.loading = false;
            }
        },
        async saveAllDeterminationData() {
            try {
                if (
                    this.takeHomeForm.no_active_substance_disorder === null ||
                    this.takeHomeForm.regular_attendance === null ||
                    this.takeHomeForm.no_behavioral_issues === null ||
                    this.takeHomeForm.no_diversion_activity === null ||
                    this.takeHomeForm.can_safely_store_medication === null
                ) {
                    alert('Please answer Yes or No for all 5 CFR take-home criteria before saving.');
                    return;
                }

                await this.$api.post('/take-home-criteria', {
                    phase_determination_id: this.id,
                    ...this.takeHomeForm
                });

                await this.$api.post('/physician-reviews', {
                    phase_determination_id: this.id,
                    review_note: this.item.physician_review.review_note,
                    reviewed_at: new Date().toISOString().split('T')[0],
                });

                await this.$api.post('/physician-determinations', {
                    phase_determination_id: this.id,
                    determination_note: this.item.physician_determination.determination_note,
                    approved_phase: this.item.physician_determination.approved_phase,
                    approved_schedule: JSON.stringify(this.item.physician_determination.approved_schedule || []),
                    pmp_report_reviewed:
                        this.item.physician_determination.pmp_report_reviewed === 'true' ||
                        this.item.physician_determination.pmp_report_reviewed === true,
                    result_notes: this.item.physician_determination.result_notes,
                    signedAt: this.signedAt,
                });

                this.editing = false;
                this.$router.back();

                // this.load();
            } catch (err) {
                const message = err.response?.data?.message;
                const errors = err.response?.data?.errors;

                if (errors) {
                    const errorList = Object.values(errors).flat().join('\n');
                    alert(`Validation Error:\n${errorList}`);
                } else {
                    alert(message || 'Failed to save data.');
                }
            }
        },
        startEditing() {
            if (!this.item.physician_review) this.item.physician_review = {};
            if (!this.item.physician_determination) this.item.physician_determination = {};

            if (!this.item.physician_review.review_note) {
                this.item.physician_review.review_note = 'Request for a phase increase: Patient is currently on phase ' + this.item.current_phase + ', patient is compliant with program and take-home rules. Client is stable on dosing. 5-point criteria reviewed; benefit outweighs the risk. No active illicit drug or alcohol use. Meets criteria for a phase increase to 6 THDs.';
            }

            if (!this.item.physician_determination.determination_note) {
                this.item.physician_determination.determination_note = 'Request for a phase increase: Patient is currently on phase ' + this.item.current_phase + ', patient is compliant with program and take-home rules. Client is stable on dosing. 5-point criteria reviewed; benefit outweighs the risk. No active illicit drug or alcohol use. Meets criteria for a phase increase to 6 THDs.';
            }

            this.editing = true;
        }
    }
};
</script>
